import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import offlineQueue from "../utils/offlineQueue";
import { flushAll } from "../utils/resilientPost";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEALTH_URL = `${API}/health`;
const PING_INTERVAL_MS = 15000;

/**
 * useBackendHealth — Pings /api/health periodically.
 * - Exposes { status: 'online'|'offline'|'checking', pendingCount, flushing }
 * - On transition offline -> online, automatically flushes the offline queue.
 */
export function useBackendHealth() {
  const [status, setStatus] = useState("checking");
  const [pendingCount, setPendingCount] = useState(() => offlineQueue.size());
  const [flushing, setFlushing] = useState(false);
  const prevStatusRef = useRef("checking");
  const flushingRef = useRef(false);

  useEffect(() => {
    const unsub = offlineQueue.subscribe((items) => {
      setPendingCount(items.length);
    });
    return unsub;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const flush = async () => {
      if (flushingRef.current) return;
      if (offlineQueue.size() === 0) return;
      flushingRef.current = true;
      setFlushing(true);
      const total = offlineQueue.size();
      try {
        const { succeeded, failed } = await flushAll();
        if (succeeded > 0) {
          toast.success(
            failed > 0
              ? `Sincronizados ${succeeded} de ${total}. ${failed} pendientes.`
              : `Sincronizados ${succeeded} registros guardados localmente.`,
            { duration: 5000 }
          );
        }
      } catch (e) {
        console.error("[useBackendHealth] flush error", e);
      } finally {
        flushingRef.current = false;
        if (!cancelled) setFlushing(false);
      }
    };

    const ping = async () => {
      try {
        await axios.get(HEALTH_URL, {
          timeout: 10000,
          _skipToast: true,
          _noRetry: true, // fail fast — don't retry, we retry on interval anyway
        });
        if (cancelled) return;
        const prev = prevStatusRef.current;
        prevStatusRef.current = "online";
        setStatus("online");
        if (typeof window !== "undefined") window.__backendHealthStatus = "online";
        if (prev === "offline") {
          toast.success("Conexión restablecida", { duration: 3000 });
          // flush queue when backend comes back
          flush();
        } else if (prev === "checking" && offlineQueue.size() > 0) {
          // app just loaded and there are pending ops -> flush
          flush();
        }
      } catch (err) {
        if (cancelled) return;
        const prev = prevStatusRef.current;
        prevStatusRef.current = "offline";
        setStatus("offline");
        if (typeof window !== "undefined") window.__backendHealthStatus = "offline";
        // Diagnostic log (useful in production console)
        // eslint-disable-next-line no-console
        console.warn("[health] backend unreachable:", err?.message || err?.code || "unknown");
        if (prev === "online") {
          toast.error("Sin conexión con el servidor. Tus datos se guardan localmente.", {
            duration: 6000,
          });
        }
      }
    };

    // Initial ping
    ping();
    const interval = setInterval(ping, PING_INTERVAL_MS);

    // Ping when window regains focus (user comes back to tab)
    const onFocus = () => ping();
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onFocus);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onFocus);
    };
  }, []);

  return { status, pendingCount, flushing };
}

export default useBackendHealth;
